import React, { useState, useCallback, useRef, useEffect, type ReactNode } from 'react';
import { X, MessageSquare, Settings, Minimize2 } from 'lucide-react';
import { ChatWindow } from './ChatWindow';
import { SessionList, type SessionListRef } from './SessionList';
import { useTranslation, type Language } from '../hooks/useTranslation';
import { useSessionWelcomeTracking } from '../hooks/useSessionWelcomeTracking';
import { cn } from '../utils/cn';
import { log } from '../utils/logger';
import { isAtBreakpoint } from '../constants/ui';
import type { 
  PollingConfig, 
  AttributionType, 
  HeaderConfig, 
  SessionHistoryConfig, 
  MobileMenuConfig,
  SettingsModalRenderProps,
  ScrollConfig
} from '../types/chat';
import type { SharedChatState, SharedChatStateSetters } from '../hooks/useSharedChatState';

// Re-export for backwards compatibility
export type { SettingsModalRenderProps };

/**
 * Props for the FullScreenChatbot component
 */
interface FullScreenChatbotProps {
  /** The URL of the chat server */
  serverUrl: string;
  /** The ID of the agent to chat with */
  agentId: string;
  /** The ID of the customer */
  customerId: string;
  /** Customer name for personalization */
  customerName?: string;
  /** Agent name for display purposes */
  agentName?: string;
  /** Authentication token for backend requests */
  authToken?: string;
  /** When true, block session creation until authToken is present */
  requireAuth?: boolean;
  /** Language for the interface */
  language?: Language;
  /** Shared session ID from parent */
  sessionId?: string | null;
  /** Whether welcome message has been shown */
  hasShownWelcome?: boolean;
  /** Callback when a session is created */
  onSessionCreated?: (sessionId: string) => void;
  /** Callback when session is updated (including clearing) */
  onSessionUpdate?: (sessionId: string | null) => void;
  /** Callback when welcome message is shown */
  onWelcomeShown?: () => void;
  /** Callback when the full-screen view is closed */
  onClose: () => void;
  /** Callback when contracting from full-screen to popup */
  onContract?: () => void;
  /** Custom polling intervals for adaptive polling (optional) */
  pollingConfig?: PollingConfig;
  /**
   * Render prop for the settings modal
   * The Chat module does not provide a settings modal - the host app must provide one
   * If not provided, the settings button will still appear but do nothing
   *
   * @example
   * ```tsx
   * renderSettingsModal={(props) => (
   *   <MyConfigurationModal
   *     isOpen={props.isOpen}
   *     onClose={props.onClose}
   *     serverUrl={props.serverUrl}
   *     // ... your additional props
   *   />
   * )}
   * ```
   */
  renderSettingsModal?: (props: SettingsModalRenderProps) => ReactNode;
  /** @deprecated Use mobileMenuConfig.showSettingsButton instead */
  showSettingsButton?: boolean;
  /** Attribution type: 'none' | 'enter1nine' (default: 'none') */
  attribution?: AttributionType;
  /** Welcome messages per language to send when creating a new session (optional) */
  welcomeMessages?: Record<'da' | 'en', string>;
  /** Header configuration for customizing buttons and appearance */
  headerConfig?: HeaderConfig;
  /** Whether debug mode is enabled (shows draft, logs, etc.) */
  debugMode?: boolean;
  /** Records scroll/bubble/cursor layout timeline for jump debugging */
  layoutDebug?: boolean;
  /** Session history sidebar configuration */
  sessionHistoryConfig?: SessionHistoryConfig;
  /** Mobile bottom menu configuration */
  mobileMenuConfig?: MobileMenuConfig;
  /** Scroll behavior configuration */
  scrollConfig?: ScrollConfig;
  /** External input message value (for controlled state - persists across mode changes) */
  inputMessage?: string;
  /** Callback for input message changes (for controlled state) */
  onInputMessageChange?: (message: string) => void;
  /** Shared chat state from parent (for state persistence across mode switches) */
  sharedState?: SharedChatState;
  /** Setters for shared chat state */
  sharedStateSetters?: SharedChatStateSetters;
}

/**
 * FullScreenChatbot component that provides a full-screen chat experience
 * with session management in the left sidebar.
 *
 * This component is part of the independent Chat module and does not
 * include a configuration modal. The host application can provide one
 * via the `renderSettingsModal` render prop.
 */
export const FullScreenChatbot: React.FC<FullScreenChatbotProps> = ({
  serverUrl,
  agentId,
  customerId,
  customerName,
  agentName,
  authToken,
  requireAuth = false,
  language = 'en',
  sessionId,
  hasShownWelcome,
  onSessionCreated,
  onWelcomeShown,
  onClose,
  onContract,
  onSessionUpdate,
  pollingConfig,
  renderSettingsModal,
  showSettingsButton,
  attribution = 'enter1nine',
  welcomeMessages,
  headerConfig,
  sessionHistoryConfig,
  mobileMenuConfig,
  scrollConfig,
  inputMessage,
  onInputMessageChange,
  debugMode = false,
  layoutDebug = false,
  sharedState,
  sharedStateSetters
}) => {
  // Scroll configuration with defaults
  const scrollToNewMessage = scrollConfig?.scrollToNewMessage !== false;
  const respectUserScroll = scrollConfig?.respectUserScroll !== false;
  
  // Session history configuration with defaults
  const sessionHistoryEnabled = sessionHistoryConfig?.enabled !== false;
  const showSessionSidebar = sessionHistoryConfig?.showSidebar !== false && sessionHistoryEnabled;
  const showMobileSessionToggle = sessionHistoryConfig?.showMobileToggle !== false && sessionHistoryEnabled;
  const sidebarWidth = sessionHistoryConfig?.sidebarWidth || 'md';
  
  // Get sidebar width class based on config
  const sidebarWidthClass = {
    'sm': 'w-56 lg:w-64',
    'md': 'w-64 lg:w-80',
    'lg': 'w-80 lg:w-96'
  }[sidebarWidth];

  // Mobile menu configuration with defaults
  const showMobileMenu = mobileMenuConfig?.enabled !== false;
  const showMobileConversationsButton = mobileMenuConfig?.showConversationsButton !== false && sessionHistoryEnabled;
  const showMobileMinimizeButton = mobileMenuConfig?.showMinimizeButton !== false;

  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(sessionId || null);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showSessions, setShowSessions] = useState<boolean>(() => {
    // Start with sessions visible on desktop only (if enabled)
    return showSessionSidebar && isAtBreakpoint('MD');
  });

  const { t } = useTranslation(language);
  const currentAgentName = agentName || t('agent.defaultName');

  // Track welcome state per session - allows skipping animation when switching sessions
  const { markWelcomeShown, hasShownWelcome: hasSessionShownWelcome } = useSessionWelcomeTracking();
  
  // Determine the effective session ID for welcome tracking
  const effectiveSessionId = selectedSessionId || sessionId || null;
  
  // Check if welcome should be skipped for the current session
  // Use parent's hasShownWelcome OR our own tracking
  const shouldSkipWelcomeAnimation = hasShownWelcome || hasSessionShownWelcome(effectiveSessionId);

  // Show settings button if explicitly enabled, in mobileMenuConfig, or if renderSettingsModal is provided
  const shouldShowSettingsButton = mobileMenuConfig?.showSettingsButton ?? showSettingsButton ?? !!renderSettingsModal;

  // Ref for SessionList to trigger refresh
  const sessionListRef = useRef<SessionListRef>(null);

  // Sync selectedSessionId with incoming sessionId when entering fullscreen
  useEffect(() => {
    if (sessionId && sessionId !== selectedSessionId) {
      log(`Syncing fullscreen session: ${sessionId}`);
      setSelectedSessionId(sessionId);
    }
  }, [sessionId, selectedSessionId]);

  /**
   * Updates the main app session - helper function
   */
  const updateMainAppSession = useCallback((sessionId: string | null) => {
    onSessionUpdate?.(sessionId);
  }, [onSessionUpdate]);

  /**
   * Handles session selection from the sidebar
   */
  const handleSessionSelect = useCallback((newSessionId: string) => {
    // Reset shared state before switching sessions to clear old messages
    if (sharedStateSetters?.resetSharedState) {
      sharedStateSetters.resetSharedState();
    }
    setSelectedSessionId(newSessionId);
    // Mark as welcome shown since existing sessions already have messages
    // This prevents the welcome animation from playing when switching sessions
    markWelcomeShown(newSessionId);
    // Update the main app's session to keep popup and fullscreen in sync
    updateMainAppSession(newSessionId);

    // Close sessions sidebar on mobile after selection
    if (!isAtBreakpoint('MD')) {
      setShowSessions(false);
    }
  }, [updateMainAppSession, markWelcomeShown, sharedStateSetters]);

  /**
   * Handles creating a new session
   */
  const handleNewSession = useCallback(() => {
    // Reset shared state to clear old messages before starting fresh
    if (sharedStateSetters?.resetSharedState) {
      sharedStateSetters.resetSharedState();
    }
    setSelectedSessionId(null);
    // Clear the main app's session to start fresh
    updateMainAppSession(null);
    // Refresh the session list when starting a new session
    sessionListRef.current?.refresh();
    log('New session button clicked, refreshing session list');
  }, [updateMainAppSession, sharedStateSetters]);

  /**
   * Handles when a new session is created in the chat interface
   */
  const handleSessionCreated = useCallback((sessionId: string) => {
    setSelectedSessionId(sessionId);
    // Update the main app's session
    updateMainAppSession(sessionId);
    // Also call the original callback for compatibility
    onSessionCreated?.(sessionId);
    // Refresh the session list to show the new session
    sessionListRef.current?.refresh();
    log('New session created, refreshing session list');
  }, [updateMainAppSession, onSessionCreated]);

  /**
   * Handles contracting from fullscreen - sync the selected session back to main app
   */
  const handleContractWithSessionSync = useCallback(() => {
    // If user selected a different session in fullscreen, update main app session
    if (selectedSessionId && selectedSessionId !== sessionId) {
      updateMainAppSession(selectedSessionId);
    }
    onContract?.();
  }, [selectedSessionId, sessionId, updateMainAppSession, onContract]);

  /**
   * Handles welcome message shown - marks session in our tracking and calls parent callback
   */
  const handleWelcomeShown = useCallback(() => {
    if (effectiveSessionId) {
      markWelcomeShown(effectiveSessionId);
    }
    onWelcomeShown?.();
  }, [effectiveSessionId, markWelcomeShown, onWelcomeShown]);

  /**
   * Handles opening/closing the settings panel
   */
  const toggleSettings = useCallback(() => {
    log('FullScreen toggleSettings called:', { currentShowSettings: showSettings });
    setShowSettings(!showSettings);
  }, [showSettings]);

  /**
   * Handles toggling the sessions sidebar
   */
  const handleToggleSessions = useCallback(() => {
    setShowSessions(!showSessions);
  }, [showSessions]);

  return (
    <div className="chatbot-fullscreen fixed inset-0 bg-background z-50 flex">
      {/* Left Sidebar - Session List (only if session history is enabled) */}
      {showSessionSidebar && showSessions && (
        <>
          {/* Desktop sidebar */}
          <div className={`hidden md:block ${sidebarWidthClass} flex-shrink-0 bg-card border-r border-border`}>
            <SessionList
              ref={sessionListRef}
              serverUrl={serverUrl}
              authToken={authToken}
              agentId={agentId}
              selectedSessionId={selectedSessionId}
              onSessionSelect={handleSessionSelect}
              onNewSession={sessionHistoryConfig?.allowNewSession !== false ? handleNewSession : undefined}
              language={language}
              customerId={customerId}
              allowDelete={sessionHistoryConfig?.allowDeleteSession !== false}
            />
          </div>

          {/* Mobile overlay */}
          <div className="chatbot-mobile-overlay md:hidden fixed inset-0 z-[70] flex">
            {/* Backdrop - must have lower z-index than sidebar */}
            <div
              className="chatbot-mobile-backdrop absolute inset-0 bg-black/50 z-0"
              onClick={handleToggleSessions}
            />
            {/* Sidebar - must have higher z-index to receive input events */}
            <div className="chatbot-mobile-sidebar relative z-10 w-full max-w-sm bg-card h-full flex flex-col" style={{ paddingTop: 'max(0rem, env(safe-area-inset-top))', paddingBottom: 'max(0rem, env(safe-area-inset-bottom))' }}>
              {/* Close Button for Mobile */}
              <div className="absolute top-4 right-4 z-10">
                <button
                  onClick={handleToggleSessions}
                  className="chatbot-mobile-close-button p-2 text-muted-foreground hover:text-foreground transition-colors bg-card rounded-full shadow-sm"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Session List */}
              <div className="flex-1 overflow-hidden">
                <SessionList
                  ref={sessionListRef}
                  serverUrl={serverUrl}
                  authToken={authToken}
                  agentId={agentId}
                  selectedSessionId={selectedSessionId}
                  onSessionSelect={handleSessionSelect}
                  onNewSession={sessionHistoryConfig?.allowNewSession !== false ? handleNewSession : undefined}
                  language={language}
                  customerId={customerId}
                  allowDelete={sessionHistoryConfig?.allowDeleteSession !== false}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Right Side - Chat Interface */}
      <div className="flex-1 flex flex-col bg-background min-w-0">
        {/* Chat Content using ChatWindow */}
        <div className="flex-1 overflow-hidden">
          <ChatWindow
            key={`chat-${selectedSessionId || sessionId || 'new'}`}
            serverUrl={serverUrl}
            agentId={agentId}
            customerId={customerId}
            customerName={customerName}
            agentName={currentAgentName}
            authToken={authToken}
            requireAuth={requireAuth}
            scrollToNewMessage={scrollToNewMessage}
            respectUserScroll={respectUserScroll}
            language={language}
            sessionId={selectedSessionId || sessionId}
            hasShownWelcome={shouldSkipWelcomeAnimation}
            onSessionCreated={handleSessionCreated}
            onWelcomeShown={handleWelcomeShown}
            onClose={onClose}
            onContract={handleContractWithSessionSync}
            onToggleSessions={showSessionSidebar ? handleToggleSessions : undefined}
            layoutMode="fullscreen"
            pollingConfig={pollingConfig}
            attribution={attribution}
            welcomeMessages={welcomeMessages}
            headerConfig={headerConfig}
            inputMessage={inputMessage}
            onInputMessageChange={onInputMessageChange}
            debugMode={debugMode}
            layoutDebug={layoutDebug}
            sharedState={sharedState}
            sharedStateSetters={sharedStateSetters}
          />
        </div>

        {/* Mobile Bottom Menu Bar - Only visible on mobile in fullscreen */}
        {showMobileMenu && (
          <div className="chatbot-mobile-menu md:hidden bg-primary border-t border-primary" style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}>
            <div className="flex items-center justify-around py-3 px-4">
              {/* Conversations Button - Only shown if session history is enabled */}
              {showMobileConversationsButton && showMobileSessionToggle && (
                <button
                  onClick={handleToggleSessions}
                  className={cn(
                    'chatbot-mobile-menu-button flex flex-col items-center space-y-1 p-2 rounded-lg transition-all duration-200',
                    showSessions
                      ? 'chatbot-mobile-menu-button-active bg-background text-primary'
                      : 'text-primary-foreground hover:bg-background hover:text-primary'
                  )}
                >
                  <MessageSquare size={20} className="chatbot-mobile-menu-button-icon" />
                  <span className="chatbot-mobile-menu-button-text text-xs font-medium">{t('conversations.title')}</span>
                </button>
              )}

              {/* Settings Button - Only shown if renderSettingsModal is provided */}
              {shouldShowSettingsButton && (
                <button
                  onClick={toggleSettings}
                  className="chatbot-mobile-menu-button flex flex-col items-center space-y-1 p-2 rounded-lg transition-all duration-200 text-primary-foreground hover:bg-background hover:text-primary"
                >
                  <Settings size={20} className="chatbot-mobile-menu-button-icon" />
                  <span className="chatbot-mobile-menu-button-text text-xs font-medium">{t('settings.title')}</span>
                </button>
              )}

              {/* Exit Fullscreen Button */}
              {showMobileMinimizeButton && (
                <button
                  onClick={handleContractWithSessionSync}
                  className="chatbot-mobile-menu-button flex flex-col items-center space-y-1 p-2 rounded-lg transition-all duration-200 text-primary-foreground hover:bg-background hover:text-primary"
                >
                  <Minimize2 size={20} className="chatbot-mobile-menu-button-icon" />
                  <span className="chatbot-mobile-menu-button-text text-xs font-medium">{t('common.minimize')}</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Settings Modal - Rendered by host application via render prop */}
      {renderSettingsModal?.({
        isOpen: showSettings,
        onClose: toggleSettings,
        serverUrl,
        agentId,
        customerId,
        language: language,
      })}
    </div>
  );
};
